/**
 * Лист согласования — печатная форма маршрута документа: решения, даты, комментарии и пустая
 * графа «Подпись» для живой подписи (стандарт заказчика «Роль | ФИО / Должность | Подпись / Дата»).
 * Строится ТОЛЬКО из карточки GET /edo/requests/:id: route[] даёт шаги и людей, tasks[] — точный
 * статус решения. PDF — печатью браузера («Сохранить как PDF»), поэтому кнопка только в вебе.
 * Всё пользовательское (тема, ФИО, комментарии) экранируется: HTML пишется в iframe, и
 * неэкранированный комментарий исполнился бы там как скрипт.
 */
import { tr, type Lang, type Localized } from '../../i18n'
import { STATUS_LABEL } from '../../lib/edo-labels'
import type { RequestCard } from '../../lib/types'
import { fmtDateTime } from './components/CardDetails'
import { GROUP_LABEL, STAGE_ACT, cardRoute, docNo, reqVM } from './processVM'

/** Решение по шагу — по статусу задачи (у route[].state «rejected» склеивает отказ и доработку). */
const DECISION: Record<string, Localized> = {
  APPROVED: { ru: 'Согласовано', en: 'Approved', az: 'Razılaşdırıldı' },
  REJECTED: { ru: 'Отклонено', en: 'Rejected', az: 'Rədd edildi' },
  REWORK: { ru: 'Возвращено на доработку', en: 'Returned for rework', az: 'Yenidən işlənməyə qaytarıldı' },
  RESUBMITTED: { ru: 'Отправлено повторно', en: 'Resubmitted', az: 'Təkrar göndərildi' },
  ESCALATED: { ru: 'Не рассмотрено в срок', en: 'Not reviewed in time', az: 'Vaxtında baxılmadı' },
  OPEN: { ru: 'На рассмотрении', en: 'Under review', az: 'Baxılır' },
}
const PENDING: Localized = { ru: 'Ожидает', en: 'Pending', az: 'Gözləyir' }

const L = {
  title: { ru: 'Лист согласования', en: 'Approval sheet', az: 'Razılaşdırma vərəqi' },
  doc: { ru: 'Документ', en: 'Document', az: 'Sənəd' },
  subject: { ru: 'Тема', en: 'Subject', az: 'Mövzu' },
  initiator: { ru: 'Инициатор', en: 'Initiator', az: 'Təşəbbüskar' },
  submittedBy: { ru: 'Подал', en: 'Submitted by', az: 'Təqdim edən' },
  created: { ru: 'Дата подачи', en: 'Submitted on', az: 'Təqdim tarixi' },
  status: { ru: 'Статус', en: 'Status', az: 'Status' },
  step: { ru: 'Этап', en: 'Step', az: 'Mərhələ' },
  person: { ru: 'ФИО / Должность', en: 'Name / Position', az: 'S.A.A. / Vəzifə' },
  decision: { ru: 'Решение', en: 'Decision', az: 'Qərar' },
  at: { ru: 'Дата и время', en: 'Date and time', az: 'Tarix və vaxt' },
  comment: { ru: 'Комментарий', en: 'Comment', az: 'Şərh' },
  signature: { ru: 'Подпись', en: 'Signature', az: 'İmza' },
  generated: { ru: 'Сформировано на портале AS Life', en: 'Generated in the AS Life portal', az: 'AS Life portalında yaradılıb' },
} satisfies Record<string, Localized>

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ESC[c])

const CSS = `
@page { size: A4; margin: 15mm; }
body { font: 11pt/1.35 Arial, sans-serif; color: #000; margin: 0; }
h1 { font-size: 16pt; margin: 0 0 10pt; }
table { width: 100%; border-collapse: collapse; }
.meta { margin-bottom: 12pt; }
.meta th { text-align: left; width: 28%; font-weight: normal; color: #444; padding: 2pt 6pt 2pt 0; vertical-align: top; }
.meta td { padding: 2pt 0; }
.route th, .route td { border: 1px solid #000; padding: 4pt; text-align: left; vertical-align: top; }
.route td { height: 12mm; }
.route th { background: #eee; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.route tr { page-break-inside: avoid; }
.comment { white-space: pre-line; overflow-wrap: anywhere; }
.sign { width: 24mm; }
.muted { color: #555; font-size: 9pt; }
.foot { margin-top: 14pt; font-size: 9pt; color: #555; }
`

export function approvalSheetHtml(
  card: RequestCard,
  opts: { lang: Lang; printedBy: string; now?: Date },
): string {
  const { lang, printedBy, now = new Date() } = opts
  const t = (v: Localized) => esc(tr(v, lang))
  const vm = reqVM(card)
  const no = docNo(vm)
  const payload = (card.payload ?? {}) as { title?: unknown }
  const subject = typeof payload.title === 'string' ? payload.title : ''
  const byTask = new Map(card.tasks.map((tk) => [tk.id, tk]))

  const rows = (cardRoute(card) ?? [])
    .filter((s) => s.state !== 'na')
    .map((s, i) => {
      const task = s.taskId ? byTask.get(s.taskId) : undefined
      const decision = task ? DECISION[String(task.status)] ?? String(task.status) : PENDING
      const name = s.person ? esc(s.person.fullName) : s.group ? t(GROUP_LABEL[s.group] ?? s.group) : '—'
      const position = s.person?.jobTitle ? `<br><span class="muted">${esc(s.person.jobTitle)}</span>` : ''
      return (
        `<tr class="row"><td>${i + 1}</td><td>${t(STAGE_ACT[s.stage] ?? s.stage)}</td>` +
        `<td>${name}${position}</td><td>${t(decision)}</td>` +
        `<td>${s.decidedAt ? fmtDateTime(s.decidedAt) : ''}</td><td class="comment">${esc(s.comment ?? '')}</td>` +
        `<td class="sign"></td></tr>`
      )
    })
    .join('')

  // У IT_WORKS номер 1С и есть номер документа (docNo) — второй раз в скобках не повторяем.
  const doc1c = card.portalRequestId && card.portalRequestId !== no ? ` (${esc(card.portalRequestId)})` : ''
  const meta: [Localized, string][] = [
    // reqVM() всегда заполняет title (titleOf() по типу/payload) — vm тут не из taskVM().
    [L.doc, `${esc(no)}${doc1c} · ${t(vm.title!)}`],
    ...(subject ? [[L.subject, esc(subject)] as [Localized, string]] : []),
    [L.initiator, esc(card.initiator.fullName) + (vm.employmentCompany ? ` · ${esc(vm.employmentCompany)}` : '')],
    ...(card.submittedBy ? [[L.submittedBy, esc(card.submittedBy.fullName)] as [Localized, string]] : []),
    [L.created, fmtDateTime(card.createdAt)],
    [L.status, t(STATUS_LABEL[card.status] ?? card.status)],
  ]

  return (
    `<!doctype html><html lang="${lang}"><head><meta charset="utf-8">` +
    `<title>${t(L.title)} ${esc(no)}</title><style>${CSS}</style></head><body>` +
    `<h1>${t(L.title)}</h1>` +
    `<table class="meta">${meta.map(([k, v]) => `<tr><th>${t(k)}</th><td>${v}</td></tr>`).join('')}</table>` +
    `<table class="route"><thead><tr><th>№</th><th>${t(L.step)}</th><th>${t(L.person)}</th>` +
    `<th>${t(L.decision)}</th><th>${t(L.at)}</th><th>${t(L.comment)}</th><th class="sign">${t(L.signature)}</th></tr></thead>` +
    `<tbody>${rows}</tbody></table>` +
    `<p class="foot">${t(L.generated)} · ${fmtDateTime(now.toISOString())} · ${esc(printedBy)}</p>` +
    `</body></html>`
  )
}

/**
 * Печать скрытым iframe: без новой вкладки, блокировщик всплывающих окон не мешает. Только веб.
 * Один переиспользуемый фрейм (id="approval-sheet-print") вместо удаления в onafterprint: в
 * Safari после-печать иногда стреляет до отрисовки диалога и печать выходит пустой, а если
 * событие не долетело вовсе — фрейм зависал в DOM. Следующий вызов сносит предыдущий фрейм сам;
 * скрытый 0×0 фрейм, ожидающий следующей печати, безобиднее.
 */
export function printApprovalSheet(html: string): void {
  document.getElementById('approval-sheet-print')?.remove()
  const frame = document.createElement('iframe')
  frame.id = 'approval-sheet-print'
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  document.body.appendChild(frame)
  const win = frame.contentWindow
  if (!win) {
    frame.remove()
    return
  }
  win.document.open()
  win.document.write(html)
  win.document.close()
  win.focus()
  // Chrome/Edge предлагают именем PDF заголовок ВЕРХНЕГО документа, а не iframe — на время
  // печати подменяем его заголовком листа (Safari/Firefox и так берут title из iframe).
  const prevTitle = document.title
  document.title = win.document.title || prevTitle
  win.print() // в Chrome/Edge блокирует до закрытия диалога печати
  document.title = prevTitle
}
